package com.eladfinish.bible.contest.screens.randomVerse.usecases

import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.TextRange
import com.eladfinish.bible.contest.common.ChaptersRepository.Companion.padNumberExt
import com.eladfinish.bible.contest.common.usecases.GetHighlightedVerseMarkupRangeUseCase
import com.eladfinish.bible.contest.common.usecases.MakeOtiotPasukSmallerUseCase
import com.eladfinish.bible.contest.data.ChapterData
import com.eladfinish.bible.contest.di.config.DebugInfo
import com.eladfinish.bible.contest.domain.catalog.BibleCatalog.books
import com.eladfinish.bible.contest.extensions.end
import com.eladfinish.bible.contest.extensions.runIf
import com.eladfinish.bible.contest.extensions.start
import com.eladfinish.bible.contest.screens.search.model.VerseUiModel
import com.eladfinish.bible.contest.text.SpanType
import com.eladfinish.bible.contest.text.SpannedRange
import com.eladfinish.bible.contest.utils.toOtiot
import io.github.aakira.napier.Napier
import kotlinx.collections.immutable.toImmutableList

open class BuildChapterDialogFromVerse(
    private val getHighlightedVerseMarkupRangeUseCase: GetHighlightedVerseMarkupRangeUseCase,
    private val hidePeopleNamesUsecase: HidePeopleNamesUsecase,
    private val getNamesToHideUseCase: GetNamesToHideUseCase,
    private val makeOtiotPasukSmallerUseCase: MakeOtiotPasukSmallerUseCase,
    private val debugInfo: DebugInfo,
) {

    /**
     * @param namesToHide when null, people names are not hidden (e.g. outside the random-verse
     * game, where guessing the book isn't part of the challenge).
     */
    open operator fun invoke(
        verseUiModel: VerseUiModel?,
        parasha: String = "",
        namesToHide: StringBuilder? = null,
    ): ChapterData {
        val item = requireNotNull(verseUiModel)

        // VerseItem.bookIndex was 1-based; VerseUiModel.bookIndex follows the same convention
        // when populated from PickVerseFromChapterUseCaseV2 via ChaptersRepository.getBookNumber().
        val bookIndex0 = item.bookIndex - 1
        val chapterHebrew = item.chapterIndex.toOtiot()

        val (content, markupRange) = getHighlightedVerseMarkupRangeUseCase(
            bookIndex = bookIndex0,
            chapter = chapterHebrew,
            otVerse = item.verse,
        )

        val spannedRanges = namesToHide?.let {
            hiddenNameRanges(
                content = content,
                chapterFilename = chapterFilename(item),
                parasha = parasha,
                namesToHide = it,
            )
        }.orEmpty()

        val spannedRangeList = buildList {
            // ORDER IS MATTER!
            addAll(elements = makeOtiotPasukSmallerUseCase(charSequence = content))
            add(SpannedRange(spanType = SpanType.BackgroundColor(Color.Yellow), textRange = markupRange))
            addAll(elements = spannedRanges)
        }

        return ChapterData(
            bookIndex = bookIndex0,
            chapterIndex = item.chapterIndex,
            content = content.runIf(debugInfo.isDebugMode && namesToHide != null) {
                plus("\n\n$namesToHide")
            },
            spannedRangeList = spannedRangeList.toImmutableList(),
        )
    }

    private fun chapterFilename(item: VerseUiModel): String {
        val english = books[item.bookIndex - 1].trans
        return "_${item.bookIndex}_$english-${item.chapterIndex.padNumberExt(english)}.txt"
    }

    private fun hiddenNameRanges(
        content: CharSequence,
        chapterFilename: String,
        parasha: String,
        namesToHide: StringBuilder,
    ): List<SpannedRange> {
        val namesToHideList = getNamesToHideUseCase(
            chapterFilename = chapterFilename,
            parasha = parasha,
            namesToHide = namesToHide,
        )
        Napier.d("getNamesToHide_ $chapterFilename : ${namesToHideList.joinToString(separator = "\n")}")

        val personRegexList = hidePeopleNamesUsecase.getVerseWithHiddenNames(
            names = namesToHideList,
            randomVerseStr = "",
        ).personRegexList
        Napier.d("getNamesToHide_ personRegexList $personRegexList")

        return personRegexList.flatMapIndexed { index, personRegex ->
            personRegex.toRegex().findAll(content).map { matchResult ->
                SpannedRange(
                    spanType = SpanType.BackgroundColorFromPalette(index),
                    textRange = TextRange(start = matchResult.start, end = matchResult.end),
                )
            }.toList()
        }
    }
}
